import { register, SwiperContainer } from 'swiper/element';
import { A11y, Autoplay, Navigation } from 'swiper/modules';
import { AutoplayOptions, SwiperOptions } from 'swiper/types';
import { getNavigationA11yParams, stripInvalidAriaControls } from './SwiperA11y';

export class HeroSliderInitializer {
	/**
	 * Initializes the Slider.
	 *
	 * @returns void
	 */
	init() {
		register();

		document.addEventListener('DOMContentLoaded', () => {
			// Get all swipers and initialize them
			const swiperElements = document.querySelectorAll<SwiperContainer>(
				'.hero-slider-container swiper-container'
			);
			if (!swiperElements.length) return;

			swiperElements.forEach((swiper: SwiperContainer) => {
				new HeroSlider(swiper);
			});
		});
	}
}

class HeroSlider {
	swiper: SwiperContainer;
	swiperContainer: HTMLElement;
	navigationTabs: HTMLElement[];
	navigationTabBG: HTMLElement;
	mobileNavigationDropdown: HTMLSelectElement;

	swiperButtonPrev: HTMLElement;
	swiperButtonNext: HTMLElement;

	constructor(swiperElement: SwiperContainer) {
		this.swiper = swiperElement;
		this.swiperContainer = this.swiper.closest('.hero-slider-container')!;

		this.navigationTabs = Array.from(
			this.swiperContainer.querySelectorAll<HTMLElement>('.tab-navigation-container .tab')
		);
		this.navigationTabBG = this.swiperContainer.querySelector<HTMLElement>(
			'.tab-navigation-container .tab-background'
		)!;
		this.mobileNavigationDropdown = this.swiperContainer.querySelector<HTMLSelectElement>(
			'.mobile-navigation-container .dropdown-navigation-container'
		)!;

		this.swiperButtonPrev = this.swiperContainer.querySelector<HTMLElement>('.swiper-button-prev')!;
		this.swiperButtonNext = this.swiperContainer.querySelector<HTMLElement>('.swiper-button-next')!;
		const navigationContainer = this.swiperContainer.querySelector<HTMLElement>('.slider-navigation');

		const swiperParams: SwiperOptions = {
			modules: [Navigation, Autoplay, A11y],
			...this.getSliderProperties(),
			slidesPerView: 1,
			slidesPerGroup: 1,
			preventInteractionOnTransition: true,
			a11y: getNavigationA11yParams(navigationContainer),
			navigation: {
				prevEl: this.swiperButtonPrev,
				nextEl: this.swiperButtonNext,
			},
		};

		Object.assign(this.swiper, swiperParams);
		this.swiper.initialize();
		stripInvalidAriaControls(this.swiperButtonPrev, this.swiperButtonNext);

		document.fonts.ready.then(() => {
			this.handleNavigationTabs();
			this.handleMobileNavigationDropdown();
		});

		window.addEventListener('resize', () => {
			this.changeNavigationTab(this.navigationTabs[this.swiper.swiper.realIndex]);
		});
	}

	/**
	 * Gets the parameters transmitted by data attributes / backend settings
	 *
	 * @returns SwiperOptions
	 */
	getSliderProperties(): SwiperOptions {
		const loop = this.swiper.getAttribute('data-loop') === 'true';
		const autoplay = this.swiper.getAttribute('data-autoplay');
		const autoplayInterval = +(this.swiper.getAttribute('data-autoplayInterval') || 3000);

		let autoplayParams: AutoplayOptions | boolean = false;
		if (autoplay != 'false') {
			autoplayParams = {
				delay: autoplayInterval,
				disableOnInteraction: true,
				pauseOnMouseEnter: true,
				stopOnLastSlide: true,
			};
		}

		const swiperConfig: SwiperOptions = {
			loop: loop,
			autoplay: autoplayParams,
		};

		return swiperConfig;
	}

	/**
	 * Handles the logic for the navigation tabs
	 *
	 * @returns void
	 */
	handleNavigationTabs() {
		if (this.navigationTabs.length <= 1) {
			this.swiperContainer.querySelector<HTMLElement>('.tab-navigation-container')?.classList.add('hidden');
		}

		this.navigationTabs.forEach((tab, index) => {
			tab.addEventListener('click', () => {
				this.swiper.swiper.slideTo(index);
				this.updateActiveTab(index);
			});
		});

		this.swiper.swiper.on('slideChange', () => {
			this.updateActiveTab(this.swiper.swiper.realIndex);
		});

		this.changeNavigationTab(this.navigationTabs[this.swiper.swiper.realIndex]);
	}

	/**
	 * Updates the active navigation tab with the index
	 *
	 * @param index
	 * @returns void
	 */
	updateActiveTab = (index: number) => {
		const activeSlide = this.swiper.querySelectorAll('swiper-slide')[this.swiper.swiper.realIndex];
		const activeSlideContent = activeSlide.querySelector('.slide-content')!;

		if (this.swiper.swiper.previousIndex < this.swiper.swiper.realIndex) {
			activeSlideContent.classList.add('fade_in_right');
		} else {
			activeSlideContent.classList.add('fade_in_left');
		}

		activeSlideContent.addEventListener('animationend', (e) => {
			activeSlideContent.classList.remove('fade_in_right', 'fade_in_left');
		});

		this.navigationTabs.forEach((t) => t.classList.remove('active')); // Remove active class from all tabs
		this.navigationTabs[this.swiper.swiper.realIndex].classList.add('active'); // Add active class to clicked tab
		this.changeNavigationTab(this.navigationTabs[this.swiper.swiper.realIndex]);
	};

	/**
	 * Changes the navigation tab to the given parameter
	 *
	 * @param tab
	 * @returns void
	 */
	changeNavigationTab(tab: HTMLElement) {
		if (!tab) {
			console.log('changeNavigationTab: Tab is not defined');
			return;
		}
		let rect = tab.getBoundingClientRect();
		this.navigationTabBG.style.width = `${rect.width}px`;
		this.navigationTabBG.style.left = `${tab.offsetLeft}px`;
	}

	handleMobileNavigationDropdown() {
		if (!this.mobileNavigationDropdown) return;

		this.mobileNavigationDropdown.addEventListener('change', (event) => {
			console.log(event);
			const eventTarget: HTMLSelectElement = event.currentTarget as HTMLSelectElement;
			const index = eventTarget.selectedIndex;

			this.swiper.swiper.slideTo(index);
			this.updateActiveTab(index);
		});

		this.swiper.swiper.on('slideChange', () => {
			this.mobileNavigationDropdown.selectedIndex = this.swiper.swiper.realIndex;
		});
	}
}
